/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useState } from 'react';
import classNames from 'classnames';

import routeConfiguration, { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge,
  ExternalLink,
  Icons,
  IconSocialMediaFacebook,
  IconSocialMediaInstagram,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import Drawer from 'react-modern-drawer';
import './Drawer.css';
import css from './TopbarMobileMenu.module.css';
import { getUserType } from '../../../../util/userDataExtraction';
import { EditListingPathName, UserType } from '../../../../util/enums';
import IconArrowRight from '../../../../components/IconArrowRight/IconArrowRight';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { createResourceLocatorString } from '../../../../util/routes';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
  } = props;

  const history = useHistory();

  const onRedirectPage = (name, params = {}) => {
    history.push(createResourceLocatorString(name, routeConfiguration(), params, {}));
  };

  const user = ensureCurrentUser(currentUser);
  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  const drawerData = (
    <>
      <div className={css.browseMenu}>
        <h2>
          <FormattedMessage id="TopbarMobileMenu.moreLinks" />
        </h2>
        <div className={css.links}>
          <div
            className={css.linkItem}
            role="button"
            onClick={() => onRedirectPage('SubscriptionPage')}
          >
            <span>
              <FormattedMessage id="TopbarDesktop.subscriptionPage" /> <IconArrowRight />
            </span>
          </div>
          <div
            className={css.linkItem}
            role="button"
            onClick={() => onRedirectPage('CMSPage', { pageId: 'about' })}
          >
            <span>
              <FormattedMessage id="TopbarMobileMenu.aboutUs" /> <IconArrowRight />
            </span>
          </div>
          <div className={css.linkItem} role="button" onClick={() => onRedirectPage('FAQPage')}>
            <span>
              <FormattedMessage id="TopbarMobileMenu.faq" /> <IconArrowRight />
            </span>
          </div>
          <div
            className={css.linkItem}
            role="button"
            onClick={() => onRedirectPage('CMSPage', { pageId: 'terms-of-service' })}
          >
            <span>
              <FormattedMessage id="TopbarMobileMenu.terms" /> <IconArrowRight />
            </span>
          </div>
          <div
            className={css.linkItem}
            role="button"
            onClick={() => onRedirectPage('CMSPage', { pageId: 'privacy-policy' })}
          >
            <span>
              <FormattedMessage id="TopbarMobileMenu.privacy" /> <IconArrowRight />
            </span>
          </div>
        </div>
      </div>

      <div className={css.socialIcons}>
        <a
          href="https://www.instagram.com/landvnv/"
          target="_blank"
          rel="noreferrer"
          className={css.iconLink}
        >
          <Icons name="instagram" />
        </a>
        <a
          href="https://www.facebook.com/profile.php?id=61574938449306"
          target="_blank"
          rel="noreferrer"
          className={css.iconLink}
        >
          <Icons name="facebook" />
        </a>
        <a
          href="http://www.youtube.com/@LandVnV"
          target="_blank"
          rel="noreferrer"
          className={css.iconLink}
        >
          <Icons name="youtube" />
        </a>
      </div>
    </>
  );

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );
    return (
      <div className={css.root}>
        <div className={css.content}>
          {login}
          {signup}
          {drawerData}
          <div className={css.customLinksWrapper}>{extraLinks}</div>
        </div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };
  const inboxTab = currentUserHasListings ? 'sales' : 'orders';

  return (
    <div className={css.root}>
      <div
        className={css.userAccount}
        role="button"
        onClick={() =>
          onRedirectPage('ProfilePage', {
            id: currentUser?.id?.uuid
          })
        }
      >
        <AvatarLarge className={css.avatar} user={currentUser} disableProfileLink />
      </div>

      <div className={css.content}>
        <div className={css.greetingSec}>
          <span className={css.greeting}>
            <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
          </span>
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <FormattedMessage id="TopbarMobileMenu.logoutLink" />
          </InlineTextButton>
        </div>
        <div className={css.links}>
          <NamedLink
            className={classNames(css.linkItem, currentPageClass(`InboxPage:${inboxTab}`))}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </NamedLink>
          <NamedLink
            className={classNames(css.linkItem, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.linkItem, currentPageClass('FavoritesPage'))}
            name="FavoritesPage"
            params={{ tab: inboxTab }}
          >
            <FormattedMessage id="TopbarDesktop.FavoritesPageLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.linkItem, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.linkItem, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>
            {/* <NamedLink className={css.linkItem} name="NewListingPage">
              <FormattedMessage id="TopbarMobileMenu.newListingLink" />
            </NamedLink> */}
          
          <div className={css.customLinksWrapper}>{extraLinks}</div>
        </div>
      </div>

      {drawerData}
    </div>
  );
};

export default TopbarMobileMenu;
